import { useEffect, useRef, useState } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchCombobox, type SearchResult } from "@/components/ui/search-combobox";
import { Input } from "@/components/ui/input";
import { searchLocations } from "@/lib/snipeit/locations";
import { searchModels } from "@/lib/snipeit/lookups";
import { searchConsumables } from "@/lib/snipeit/consumables";
import { useSettings, type Theme, MAX_DASHBOARD_MODELS, type SnipeItModel } from "@/context/settings-context";
import { useLocationDetail } from "@/common/api/use-location-detail";
import type { SnipeItLocation, SnipeItConsumable } from "@/lib/snipeit/types";
import { Moon, Sun, X } from "lucide-react";
import { Link } from "wouter";

export function SettingsView() {
  const {
    theme,
    setTheme,
    setLocationId,
    apiToken,
    setApiToken,
    dashboardModels,
    setDashboardModels,
    consumableBlacklist,
    setConsumableBlacklist,
    deviceBlacklist,
    setDeviceBlacklist,
  } = useSettings();
  const { location: fetchedLocation } = useLocationDetail();
  const [pickedLocation, setPickedLocation] = useState<SnipeItLocation | null>(null);
  const [locationCleared, setLocationCleared] = useState(false);
  const activeLocation = locationCleared ? null : (pickedLocation ?? fetchedLocation);
  // Track whether the user has typed in the combobox (vs. showing the fetched name)
  const [locationInput, setLocationInput] = useState<string | null>(null);
  const [tokenInput, setTokenInput] = useState(apiToken);
  const [modelInput, setModelInput] = useState("");
  const [blacklistInput, setBlacklistInput] = useState("");
  const [deviceBlacklistInput, setDeviceBlacklistInput] = useState("");
  const [saved, setSaved] = useState(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // If the user hasn't manually typed, show the active location name (or empty)
  const displayedLocationInput = locationInput ?? activeLocation?.name ?? "";

  useEffect(() => {
    return () => {
      if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
    };
  }, []);

  function handleLocationSelect(result: SearchResult) {
    const loc = result.data as SnipeItLocation;
    setPickedLocation(loc);
    setLocationCleared(false);
    setLocationInput(null); // reset to derived mode — will show loc.name via activeLocation
  }

  function handleClearLocation() {
    setPickedLocation(null);
    setLocationCleared(true);
    setLocationInput(null);
  }

  function handleSave() {
    setLocationId(activeLocation?.id ?? null);
    setPickedLocation(null);
    setLocationCleared(activeLocation === null);
    setLocationInput(null);
    setApiToken(tokenInput);
    setDashboardModels(dashboardModels);
    setConsumableBlacklist(consumableBlacklist);
    setDeviceBlacklist(deviceBlacklist);
    setSaved(true);
    if (savedTimerRef.current !== null) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSaved(false), 1500);
  }

  function handleAddModel(result: SearchResult) {
    const model = result.data as SnipeItModel;
    if (dashboardModels.length >= MAX_DASHBOARD_MODELS) return;
    if (dashboardModels.some((m) => m.id === model.id)) return;
    setDashboardModels([...dashboardModels, model]);
    setModelInput("");
  }

  function handleRemoveModel(modelId: number) {
    setDashboardModels(dashboardModels.filter((m) => m.id !== modelId));
  }

  function handleAddBlacklistItem(result: SearchResult) {
    const consumable = result.data as SnipeItConsumable;
    if (consumableBlacklist.some((b) => b.id === consumable.id)) return;
    setConsumableBlacklist([...consumableBlacklist, { id: consumable.id, name: consumable.name }]);
    setBlacklistInput("");
  }

  function handleRemoveBlacklistItem(id: number) {
    setConsumableBlacklist(consumableBlacklist.filter((b) => b.id !== id));
  }

  function handleAddDeviceBlacklistItem(result: SearchResult) {
    const model = result.data as SnipeItModel;
    if (deviceBlacklist.some((b) => b.id === model.id)) return;
    setDeviceBlacklist([...deviceBlacklist, { id: model.id, name: model.name }]);
    setDeviceBlacklistInput("");
  }

  function handleRemoveDeviceBlacklistItem(id: number) {
    setDeviceBlacklist(deviceBlacklist.filter((b) => b.id !== id));
  }

  function toggleTheme() {
    const next: Theme = theme === "light" ? "dark" : "light";
    setTheme(next);
  }

  return (
    <main className="bg-background flex min-h-dvh items-start justify-center overflow-y-auto px-4 py-6">
      <div className="w-full max-w-5xl space-y-6">
        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-3">
          <div className="space-y-6">
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">Darstellung</CardTitle>
              </CardHeader>
              <CardContent>
                <button
                  onClick={toggleTheme}
                  className="border-border bg-card text-foreground hover:bg-accent flex w-full items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium transition-colors"
                >
                  <span>{theme === "light" ? "Helles Design" : "Dunkles Design"}</span>
                  {theme === "light" ? (
                    <Moon className="text-muted-foreground h-4 w-4" />
                  ) : (
                    <Sun className="text-muted-foreground h-4 w-4" />
                  )}
                </button>
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">Tablet-Standort</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground text-xs">
                  Wird beim Zurücknehmen von Geräten als Standort vorausgewählt.
                </p>
                <div className="space-y-1">
                  <label className="text-muted-foreground text-xs font-medium">Standort</label>
                  <SearchCombobox
                    placeholder="Standort suchen…"
                    queryKey="locations"
                    searchFn={(query) =>
                      searchLocations(query).then((results) =>
                        results.map((location) => ({ id: location.id, label: location.name, data: location })),
                      )
                    }
                    value={displayedLocationInput}
                    onInputChange={(v) => setLocationInput(v)}
                    onSelect={handleLocationSelect}
                  />
                </div>
                {activeLocation && displayedLocationInput === activeLocation.name && (
                  <div className="bg-primary/10 text-primary flex items-center justify-between rounded-lg px-3 py-2 text-xs">
                    <span>Ausgewählt: {activeLocation.name}</span>
                    <button
                      type="button"
                      onClick={handleClearLocation}
                      className="hover:text-destructive transition-colors"
                      aria-label="Standort entfernen"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">Verbrauchsgüter ausblenden</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground text-xs">
                  Verbrauchsgüter, die auf der Startseite nicht als niedrig angezeigt werden sollen.
                </p>

                <div className="space-y-1">
                  <label className="text-muted-foreground text-xs font-medium">Verbrauchsgut hinzufügen</label>
                  <SearchCombobox
                    placeholder="Verbrauchsgut suchen…"
                    queryKey="consumable-blacklist"
                    searchFn={(query) =>
                      searchConsumables(query).then((results) =>
                        results
                          .filter((c) => !consumableBlacklist.some((b) => b.id === c.id))
                          .map((c) => ({ id: c.id, label: c.name, data: c })),
                      )
                    }
                    value={blacklistInput}
                    onInputChange={setBlacklistInput}
                    onSelect={handleAddBlacklistItem}
                  />
                </div>

                {consumableBlacklist.length > 0 && (
                  <div className="space-y-1">
                    {consumableBlacklist.map((item) => (
                      <div
                        key={item.id}
                        className="bg-destructive/10 text-destructive flex items-center justify-between rounded-lg px-3 py-2 text-xs"
                      >
                        <span>{item.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveBlacklistItem(item.id)}
                          className="hover:text-foreground transition-colors"
                          aria-label="Aus Blacklist entfernen"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">Dashboard-Geräte</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground text-xs">
                  Bis zu {MAX_DASHBOARD_MODELS} Gerätemodelle für das Dashboard auf der Startseite auswählen.
                </p>

                {dashboardModels.length < MAX_DASHBOARD_MODELS && (
                  <div className="space-y-1">
                    <label className="text-muted-foreground text-xs font-medium">Modell hinzufügen</label>
                    <SearchCombobox
                      placeholder="Modell suchen…"
                      queryKey="dashboard-models"
                      searchFn={(query) =>
                        searchModels(query).then((results) =>
                          results
                            .filter((m) => !dashboardModels.some((dm) => dm.id === m.id))
                            .map((m) => ({ id: m.id, label: m.name, data: m })),
                        )
                      }
                      value={modelInput}
                      onInputChange={setModelInput}
                      onSelect={handleAddModel}
                    />
                  </div>
                )}

                {dashboardModels.length > 0 && (
                  <div className="space-y-1">
                    {dashboardModels.map((model) => (
                      <div
                        key={model.id}
                        className="bg-primary/10 text-primary flex items-center justify-between rounded-lg px-3 py-2 text-xs"
                      >
                        <span>{model.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveModel(model.id)}
                          className="hover:text-destructive transition-colors"
                          aria-label={`${model.name} entfernen`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">Geräte ausblenden</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground text-xs">
                  Gerätemodelle, die auf der Startseite nicht als niedrig angezeigt werden sollen.
                </p>

                <div className="space-y-1">
                  <label className="text-muted-foreground text-xs font-medium">Modell hinzufügen</label>
                  <SearchCombobox
                    placeholder="Modell suchen…"
                    queryKey="device-blacklist"
                    searchFn={(query) =>
                      searchModels(query).then((results) =>
                        results
                          .filter((m) => !deviceBlacklist.some((b) => b.id === m.id))
                          .map((m) => ({ id: m.id, label: m.name, data: m })),
                      )
                    }
                    value={deviceBlacklistInput}
                    onInputChange={setDeviceBlacklistInput}
                    onSelect={handleAddDeviceBlacklistItem}
                  />
                </div>

                {deviceBlacklist.length > 0 && (
                  <div className="space-y-1">
                    {deviceBlacklist.map((item) => (
                      <div
                        key={item.id}
                        className="bg-destructive/10 text-destructive flex items-center justify-between rounded-lg px-3 py-2 text-xs"
                      >
                        <span>{item.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDeviceBlacklistItem(item.id)}
                          className="hover:text-foreground transition-colors"
                          aria-label="Aus Blacklist entfernen"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">API-Token</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-muted-foreground text-xs">
                  Snipe-IT API-Token für die Verbindung zum Inventarsystem.
                </p>
                <div className="space-y-1">
                  <label className="text-muted-foreground text-xs font-medium">Token</label>
                  <Input
                    type="password"
                    placeholder="API-Token eingeben…"
                    value={tokenInput}
                    onChange={(event) => setTokenInput(event.target.value)}
                  />
                </div>
                {tokenInput && (
                  <div className="bg-primary/10 text-primary rounded-lg px-3 py-2 text-xs">Token gesetzt</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Link href="/" className={buttonVariants({ variant: "secondary", size: "sm" })}>
            Zurück
          </Link>
          <Button size="sm" onClick={handleSave} disabled={saved}>
            {saved ? "Gespeichert ✓" : "Speichern"}
          </Button>
        </div>
      </div>
    </main>
  );
}
